// All pickleball matches here are doubles (2v2), never singles.

// Sort players descending by rank value, then split into `courtCount` groups
// as evenly as possible. Any remainder players land in the earliest (top) groups.
export function splitIntoGroups(rankedPlayerIds, courtCount) {
  var n = rankedPlayerIds.length;
  var base = Math.floor(n / courtCount);
  var remainder = n % courtCount;
  var groups = [];
  var idx = 0;
  for (var c = 0; c < courtCount; c++) {
    var size = base + (remainder > 0 ? 1 : 0);
    if (remainder > 0) remainder--;
    groups.push(rankedPlayerIds.slice(idx, idx + size));
    idx += size;
  }
  return groups;
}

// Fisher-Yates shuffle.
export function shuffle(arr) {
  var a = arr.slice();
  for (var i = a.length - 1; i > 0; i--) {
    var j = Math.floor(Math.random() * (i + 1));
    var tmp = a[i]; a[i] = a[j]; a[j] = tmp;
  }
  return a;
}

function pairKey(a, b) { return a < b ? a + "|" + b : b + "|" + a; }

// Round-robin doubles for a group of n (n >= 4) sharing one court, played
// sequentially. Each round picks the 4 players with the fewest games so far,
// then partners them the way that repeats the fewest existing partnerships —
// for n=4 this reproduces the classic 3-match "everyone partners with everyone
// once" schedule; for n=5 it reproduces the standard 5-match bye-rotation
// schedule; larger groups degrade gracefully to a fair, partner-varied schedule.
export function doublesRoundRobin(playerIds) {
  var n = playerIds.length;
  if (n < 4) return [];

  var totalPairs = (n * (n - 1)) / 2;
  var targetRounds = Math.ceil(totalPairs / 2);

  var partnerCount = {};
  var gamesPlayed = {};
  playerIds.forEach(function (id) { gamesPlayed[id] = 0; });

  var matches = [];
  for (var round = 0; round < targetRounds; round++) {
    var ordered = shuffle(playerIds).sort(function (a, b) {
      return gamesPlayed[a] - gamesPlayed[b];
    });
    var four = ordered.slice(0, 4);

    var splits = [
      [[four[0], four[1]], [four[2], four[3]]],
      [[four[0], four[2]], [four[1], four[3]]],
      [[four[0], four[3]], [four[1], four[2]]],
    ];
    var best = splits[0];
    var bestScore = Infinity;
    for (var i = 0; i < splits.length; i++) {
      var split = splits[i];
      var score = (partnerCount[pairKey(split[0][0], split[0][1])] || 0) +
                  (partnerCount[pairKey(split[1][0], split[1][1])] || 0);
      if (score < bestScore) { bestScore = score; best = split; }
    }

    var teamA = best[0], teamB = best[1];
    matches.push({ teamA: teamA, teamB: teamB });

    partnerCount[pairKey(teamA[0], teamA[1])] = (partnerCount[pairKey(teamA[0], teamA[1])] || 0) + 1;
    partnerCount[pairKey(teamB[0], teamB[1])] = (partnerCount[pairKey(teamB[0], teamB[1])] || 0) + 1;
    teamA.concat(teamB).forEach(function (id) { gamesPlayed[id]++; });
  }
  return matches;
}

function teamsMatch(a, b) {
  var a0 = a.slice().sort().join(","), b0 = b.slice().sort().join(",");
  return a0 === b0;
}

// `rounds` is an array of {teamA, teamB} objects rather than [teamA, teamB]
// tuples — Firestore rejects arrays nested directly inside arrays, so pairing
// history has to be stored as objects, not array-of-array-of-array.
function repeatsAnyRound(teamA, teamB, rounds) {
  for (var r = 0; r < rounds.length; r++) {
    var lr = rounds[r];
    if ((teamsMatch(lr.teamA, teamA) && teamsMatch(lr.teamB, teamB)) || (teamsMatch(lr.teamA, teamB) && teamsMatch(lr.teamB, teamA))) {
      return true;
    }
  }
  return false;
}

// Picks one round of random-but-fair doubles pairings for social play, given
// the running `stats` accumulated over the whole session so far:
//   { gamesPlayed: {uid: n}, partnerCount: {"a|b": n}, opponentCount: {"a|b": n}, lastRoundTeams: [{teamA,teamB}, ...] }
// Two fairness rules drive it, addressing the two complaints directly:
//   1. Who plays this round is chosen by fewest games played so far (ties
//      broken randomly) — so nobody can sit out twice while someone else never
//      does; it self-corrects every round regardless of session length.
//   2. Within each court's group of 4, the team split (of the 3 possible) is
//      whichever has partnered/faced each other the least so far, with a hard
//      penalty against exactly repeating last round's matchup — so the same
//      pair doesn't run it back twice in a row, and partnerships/opponents
//      spread out over the night instead of clustering.
// Returns { courts, sitOut, stats } — `stats` is the new running total to
// persist and pass back in next time this is called.
export function nextSocialRound(playerIds, courtCount, stats) {
  var gamesPlayed = (stats && stats.gamesPlayed) || {};
  var partnerCount = (stats && stats.partnerCount) || {};
  var opponentCount = (stats && stats.opponentCount) || {};
  var lastRoundTeams = (stats && stats.lastRoundTeams) || [];

  function played(uid) { return gamesPlayed[uid] || 0; }

  var ranked = shuffle(playerIds).sort(function (a, b) { return played(a) - played(b); });
  var maxCourts = Math.min(courtCount, Math.floor(playerIds.length / 4));
  var numPlaying = maxCourts * 4;
  var playing = ranked.slice(0, numPlaying);
  var sitOut = ranked.slice(numPlaying);

  var shuffledPlaying = shuffle(playing);
  var courts = [];
  for (var c = 0; c < maxCourts; c++) {
    var four = shuffledPlaying.slice(c * 4, c * 4 + 4);
    var splits = [
      [[four[0], four[1]], [four[2], four[3]]],
      [[four[0], four[2]], [four[1], four[3]]],
      [[four[0], four[3]], [four[1], four[2]]],
    ];
    var best = splits[0];
    var bestScore = Infinity;
    for (var i = 0; i < splits.length; i++) {
      var teamA = splits[i][0], teamB = splits[i][1];
      var score = (partnerCount[pairKey(teamA[0], teamA[1])] || 0) * 2
                + (partnerCount[pairKey(teamB[0], teamB[1])] || 0) * 2
                + (opponentCount[pairKey(teamA[0], teamB[0])] || 0)
                + (opponentCount[pairKey(teamA[0], teamB[1])] || 0)
                + (opponentCount[pairKey(teamA[1], teamB[0])] || 0)
                + (opponentCount[pairKey(teamA[1], teamB[1])] || 0);
      if (repeatsAnyRound(teamA, teamB, lastRoundTeams)) score += 100;
      if (score < bestScore) { bestScore = score; best = { teamA: teamA, teamB: teamB }; }
    }
    courts.push({ court: c + 1, teamA: best.teamA, teamB: best.teamB });
  }

  var newGamesPlayed = { ...gamesPlayed };
  var newPartnerCount = { ...partnerCount };
  var newOpponentCount = { ...opponentCount };
  var newLastRoundTeams = [];
  courts.forEach(function (c) {
    c.teamA.concat(c.teamB).forEach(function (uid) { newGamesPlayed[uid] = (newGamesPlayed[uid] || 0) + 1; });
    var pkA = pairKey(c.teamA[0], c.teamA[1]);
    var pkB = pairKey(c.teamB[0], c.teamB[1]);
    newPartnerCount[pkA] = (newPartnerCount[pkA] || 0) + 1;
    newPartnerCount[pkB] = (newPartnerCount[pkB] || 0) + 1;
    c.teamA.forEach(function (a) {
      c.teamB.forEach(function (b) {
        var ok = pairKey(a, b);
        newOpponentCount[ok] = (newOpponentCount[ok] || 0) + 1;
      });
    });
    newLastRoundTeams.push({ teamA: c.teamA, teamB: c.teamB });
  });

  return {
    courts: courts,
    sitOut: sitOut,
    stats: {
      gamesPlayed: newGamesPlayed,
      partnerCount: newPartnerCount,
      opponentCount: newOpponentCount,
      lastRoundTeams: newLastRoundTeams,
    },
  };
}
